import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, RotateCcw, Archive } from "lucide-react";
import { toast } from "sonner";

const btn = "h-8 px-3 rounded-sm text-xs font-medium inline-flex items-center gap-1.5 transition-colors duration-150 disabled:opacity-50";

export const BulkDeleteBar = ({ selected, allMatching, total, pageCount, onSelectAll, onClear, filters, onDone }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const count = allMatching ? total : selected.length;
  const run = async () => {
    setBusy(true);
    try {
      const body = allMatching ? { filters } : { lead_ids: selected };
      const { data } = await api.post("/leads/bulk-delete", body);
      toast.success(`${data.deleted.toLocaleString("en-IN")} lead(s) moved to Deleted · restorable for ${data.retention_days} days`);
      setConfirm(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!count) return null;
  return (
    <div data-testid="leads-bulk-bar" className="sticky top-2 z-20 flex flex-wrap items-center gap-3 border border-clay/40 bg-white shadow-sm rounded-sm px-4 py-2.5 text-sm text-forest">
      <span data-testid="leads-bulk-count"><b>{count.toLocaleString("en-IN")}</b> lead{count === 1 ? "" : "s"} selected</span>
      {!allMatching && selected.length >= pageCount && total > pageCount && (
        <button data-testid="leads-bulk-select-all" onClick={onSelectAll} className="text-xs font-bold underline underline-offset-2 hover:text-clay">Select all {total.toLocaleString("en-IN")} matching</button>
      )}
      <button data-testid="leads-bulk-clear" onClick={onClear} className="text-xs text-forest/60 hover:text-forest">Clear</button>
      <button data-testid="leads-bulk-delete" onClick={() => setConfirm(true)} className={`${btn} ml-auto bg-clay text-white hover:bg-clay/90`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Delete {count.toLocaleString("en-IN")} lead{count === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
          <div className="text-sm text-forest/70">They'll move to <b>Deleted leads</b> with their follow-ups and site visits, and can be restored for 30 days. After that they're erased for good.</div>
          <DialogFooter>
            <button onClick={() => setConfirm(false)} className={`${btn} border border-[#E6E4DD] text-forest`}>Cancel</button>
            <button data-testid="leads-bulk-delete-confirm" disabled={busy} onClick={run} className={`${btn} bg-clay text-white`}>{busy ? "Deleting…" : "Delete"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const DeletedLeadsDialog = ({ onRestored }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const r = await api.get("/leads/deleted", { params: { limit: 200 } });
    setRows(r.data); setTotal(Number(r.headers["x-total-count"] || r.data.length)); setPicked([]);
  };
  useEffect(() => { if (open) load(); }, [open]);
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads/restore", { lead_ids: picked });
      toast.success(`${data.restored} lead(s) restored`);
      load(); onRestored();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <>
      <button data-testid="leads-deleted-open" onClick={() => setOpen(true)} className={`${btn} h-9 border border-[#E6E4DD] text-forest hover:border-forest`}><Archive className="h-3.5 w-3.5" /> Deleted leads</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Deleted leads · {total.toLocaleString("en-IN")}</DialogTitle></DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto divide-y divide-[#E6E4DD] border border-[#E6E4DD] rounded-sm" data-testid="leads-deleted-list">
            {rows.length > 0 && (
              <label className="flex items-center gap-3 px-3 py-2 bg-bone-alt/60 text-[10px] uppercase tracking-[0.15em] font-bold text-forest/70">
                <Checkbox data-testid="leads-deleted-select-all" checked={picked.length === rows.length} onCheckedChange={(v) => setPicked(v ? rows.map((r) => r.id) : [])} /> Select all shown
              </label>
            )}
            {rows.map((r) => (
              <label key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-bone-alt/30">
                <Checkbox data-testid={`leads-deleted-check-${r.id}`} checked={picked.includes(r.id)} onCheckedChange={() => toggle(r.id)} />
                <span className="flex-1 text-forest truncate">{r.lead?.name} <span className="text-forest/50 text-xs">{r.lead?.phone}</span></span>
                <span className="text-xs text-forest/50">{r.days_left} day{r.days_left === 1 ? "" : "s"} left</span>
              </label>
            ))}
            {rows.length === 0 && <div className="p-8 text-center text-sm text-forest/50">No deleted leads in this organisation.</div>}
          </div>
          {total > rows.length && <div className="text-xs text-forest/50">Showing latest {rows.length} of {total.toLocaleString("en-IN")}</div>}
          <DialogFooter>
            <button data-testid="leads-restore-btn" disabled={!picked.length || busy} onClick={restore} className={`${btn} bg-forest text-white`}><RotateCcw className="h-3.5 w-3.5" /> {busy ? "Restoring…" : `Restore ${picked.length || ""}`}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
